import React, { useCallback, useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
interface BuyerInfo {
  name: string;
  city: string;
  state: string;
}
const NAMES = ['James W.', 'Maria G.', 'Robert L.', 'Jennifer K.', 'Carlos M.', 'Emily R.', 'Daniel P.', 'Sarah T.', 'Michael B.', 'Laura S.', 'Andrew H.', 'Jessica N.', 'Thomas C.', 'Amanda F.', 'Christopher D.', 'Sophia V.', 'Matthew J.', 'Olivia A.', 'Brandon E.', 'Rachel M.'];
const LOCATIONS: {
  city: string;
  state: string;
}[] = [{
  city: 'Austin',
  state: 'TX'
}, {
  city: 'Miami',
  state: 'FL'
}, {
  city: 'Denver',
  state: 'CO'
}, {
  city: 'Portland',
  state: 'OR'
}, {
  city: 'Nashville',
  state: 'TN'
}, {
  city: 'San Diego',
  state: 'CA'
}, {
  city: 'Chicago',
  state: 'IL'
}, {
  city: 'Seattle',
  state: 'WA'
}, {
  city: 'Phoenix',
  state: 'AZ'
}, {
  city: 'Atlanta',
  state: 'GA'
}, {
  city: 'Boston',
  state: 'MA'
}, {
  city: 'Charlotte',
  state: 'NC'
}, {
  city: 'Dallas',
  state: 'TX'
}, {
  city: 'Minneapolis',
  state: 'MN'
}, {
  city: 'Tampa',
  state: 'FL'
}, {
  city: 'Raleigh',
  state: 'NC'
}, {
  city: 'Salt Lake City',
  state: 'UT'
}, {
  city: 'San Francisco',
  state: 'CA'
}, {
  city: 'New York',
  state: 'NY'
}, {
  city: 'Los Angeles',
  state: 'CA'
}];
const EDITION_KEYWORDS = ['digital edition', 'pack', 'premium', 'bundle', 'version', 'physical', 'ebook', 'audiobook'];
function randomBetween(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function detectProductName(): string {
  const fallback = 'Who We Are, Who They Are, and What They Hide';
  const h1 = document.querySelector('h1');
  if (h1) {
    const text = h1.textContent?.trim().replace(/["""]/g, '').trim();
    if (text && text.length > 5) return text;
  }
  const h2s = document.querySelectorAll('h2');
  for (const h2 of Array.from(h2s)) {
    const rect = h2.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.height > 0) {
      const text = h2.textContent?.trim();
      if (text && text.length > 5) return text;
    }
  }
  const selectors = ['[class*="product"]', '[class*="title"]', '[class*="offer"]', '[class*="headline"]'];
  for (const sel of selectors) {
    const el = document.querySelector(sel);
    if (el) {
      const text = el.textContent?.trim();
      if (text && text.length > 5 && text.length < 200) return text;
    }
  }
  return fallback;
}
function detectEdition(): string {
  const fallback = 'Digital Edition';
  const searchAreas = ['#get-access', '[class*="pricing"]', '[class*="checkout"]', '[class*="offer"]', '[class*="pack"]'];
  for (const selector of searchAreas) {
    const area = document.querySelector(selector);
    if (!area) continue;
    const text = area.textContent?.toLowerCase() || '';
    if (text.includes('complete pack') || text.includes('the pack')) return 'Complete Pack';
    if (text.includes('premium pack') || text.includes('premium bundle')) return 'Premium Pack';
    if (text.includes('bundle')) return 'Bundle';
    if (text.includes('physical')) return 'Physical Edition';
    for (const keyword of EDITION_KEYWORDS) {
      if (text.includes(keyword)) {
        if (keyword === 'ebook' && text.includes('audiobook')) return 'Digital Edition';
        if (keyword === 'pack') return 'Complete Pack';
        return keyword.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      }
    }
  }
  // Broader scan
  const allText = document.body.textContent?.toLowerCase() || '';
  if (allText.includes('ebook') && allText.includes('audiobook')) return 'Digital Edition';
  if (allText.includes('complete pack')) return 'Complete Pack';
  return fallback;
}
function detectPrice(): string {
  const fallback = '$17';
  const pricePattern = /\$\d+(?:\.\d{2})?/;
  // Priority 1: CTA area
  const ctaArea = document.querySelector('#get-access');
  if (ctaArea) {
    const els = ctaArea.querySelectorAll('span, div, p, h2, h3, h4');
    for (const el of Array.from(els)) {
      const text = el.textContent?.trim() || '';
      if (text.length > 100) continue;
      const match = text.match(pricePattern);
      if (match && !text.includes('Regular')) {
        const style = window.getComputedStyle(el);
        if (style.textDecoration !== 'line-through') {
          const fontSize = parseFloat(style.fontSize);
          if (fontSize >= 20) return match[0];
        }
      }
    }
  }
  // Priority 2: Large price text anywhere
  const allElements = document.querySelectorAll('span, p, div, h2, h3, h4, a, button');
  for (const el of Array.from(allElements)) {
    const text = el.textContent?.trim() || '';
    if (text.length > 100) continue;
    const match = text.match(pricePattern);
    if (match) {
      const style = window.getComputedStyle(el);
      if (style.textDecoration !== 'line-through' && !text.includes('Regular')) {
        const fontSize = parseFloat(style.fontSize);
        if (fontSize >= 20) return match[0];
      }
    }
  }
  // Priority 3: Any visible price
  for (const el of Array.from(allElements)) {
    const text = el.textContent?.trim() || '';
    if (text.length > 100) continue;
    const match = text.match(pricePattern);
    if (match) {
      const style = window.getComputedStyle(el);
      if (style.textDecoration !== 'line-through' && !text.includes('Regular')) {
        return match[0];
      }
    }
  }
  return fallback;
}
function detectThumbnail(): string {
  const fallback = '';
  // Look for hero/book cover images
  const imgs = document.querySelectorAll('img');
  for (const img of Array.from(imgs)) {
    const alt = (img.alt || '').toLowerCase();
    const src = img.src || '';
    if (alt.includes('book') || alt.includes('cover') || alt.includes('portada') || src.includes('portada') || src.includes('cover')) {
      return src;
    }
  }
  // Fallback: first meaningful image in hero area
  const heroSection = document.querySelector('section');
  if (heroSection) {
    const img = heroSection.querySelector('img');
    if (img && img.src) return img.src;
  }
  return fallback;
}
function detectThemeAccent(): string {
  const fallback = '#14b8a6';
  const ctaButtons = document.querySelectorAll('a, button');
  for (const btn of Array.from(ctaButtons)) {
    const style = window.getComputedStyle(btn);
    const bg = style.backgroundColor;
    if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent' && bg !== 'rgb(0, 0, 0)') {
      return bg;
    }
  }
  return fallback;
}
export function RecentPurchaseToast() {
  const [isVisible, setIsVisible] = useState(false);
  const [detectedEdition, setDetectedEdition] = useState('');
  const [detectedPrice, setDetectedPrice] = useState('');
  const [thumbnailSrc, setThumbnailSrc] = useState('');
  const [buyer, setBuyer] = useState<BuyerInfo>({
    name: '',
    city: '',
    state: ''
  });
  const [dismissed, setDismissed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isBoosted, setIsBoosted] = useState(false);
  const cycleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const boostTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const mountedRef = useRef(true);
  const lastExitIntentRef = useRef(0);
  const isBoostedRef = useRef(false);
  const isVisibleRef = useRef(false);
  const lastScrollYRef = useRef(0);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Keep refs in sync
  useEffect(() => {
    isBoostedRef.current = isBoosted;
  }, [isBoosted]);
  useEffect(() => {
    isVisibleRef.current = isVisible;
  }, [isVisible]);
  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const generateBuyer = useCallback((): BuyerInfo => {
    const name = pickRandom(NAMES);
    const location = pickRandom(LOCATIONS);
    return {
      name,
      city: location.city,
      state: location.state
    };
  }, []);
  const clearTimers = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (cycleTimerRef.current) clearTimeout(cycleTimerRef.current);
  }, []);
  const scheduleNext = useCallback(() => {
    const interval = isBoostedRef.current ? randomBetween(8000, 12000) : randomBetween(18000, 25000);
    cycleTimerRef.current = setTimeout(() => {
      if (mountedRef.current && !dismissed) showNotification();
    }, interval);
  }, [dismissed]);
  const showNotification = useCallback(() => {
    if (!mountedRef.current || dismissed) return;
    setBuyer(generateBuyer());
    const freshPrice = detectPrice();
    setDetectedPrice(freshPrice);
    setIsVisible(true);
    const displayDuration = randomBetween(6000, 8000);
    hideTimerRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      setIsVisible(false);
      scheduleNext();
    }, displayDuration);
  }, [dismissed, generateBuyer, scheduleNext]);
  const triggerExitIntent = useCallback(() => {
    const now = Date.now();
    if (now - lastExitIntentRef.current < 60000) return;
    lastExitIntentRef.current = now;
    // If not currently visible, show immediately
    if (!isVisibleRef.current && mountedRef.current && !dismissed) {
      clearTimers();
      showNotification();
    }
    // Activate boosted mode
    setIsBoosted(true);
    isBoostedRef.current = true;
    if (boostTimerRef.current) clearTimeout(boostTimerRef.current);
    const boostDuration = randomBetween(45000, 60000);
    boostTimerRef.current = setTimeout(() => {
      setIsBoosted(false);
      isBoostedRef.current = false;
    }, boostDuration);
  }, [dismissed, clearTimers, showNotification]);
  const handleClose = useCallback(() => {
    setIsVisible(false);
    setDismissed(true);
    clearTimers();
    if (boostTimerRef.current) clearTimeout(boostTimerRef.current);
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
  }, [clearTimers]);
  // Initial detection + start cycle
  useEffect(() => {
    mountedRef.current = true;
    const initTimeout = setTimeout(() => {
      if (!mountedRef.current) return;
      const edition = detectEdition();
      const price = detectPrice();
      const thumb = detectThumbnail();
      setDetectedEdition(edition);
      setDetectedPrice(price);
      setThumbnailSrc(thumb);
      const initialDelay = randomBetween(8000, 12000);
      cycleTimerRef.current = setTimeout(() => {
        if (mountedRef.current) showNotification();
      }, initialDelay);
    }, 1000);
    return () => {
      mountedRef.current = false;
      clearTimeout(initTimeout);
      if (cycleTimerRef.current) clearTimeout(cycleTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (boostTimerRef.current) clearTimeout(boostTimerRef.current);
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    };
  }, [showNotification]);
  // MutationObserver for live price + edition updates
  useEffect(() => {
    observerRef.current = new MutationObserver(() => {
      const newPrice = detectPrice();
      setDetectedPrice(prev => prev !== newPrice ? newPrice : prev);
      const newEdition = detectEdition();
      setDetectedEdition(prev => prev !== newEdition ? newEdition : prev);
    });
    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);
  // Exit intent: Desktop — mouse near top + tab visibility
  useEffect(() => {
    if (dismissed) return;
    let lastY = 0;
    let lastTime = Date.now();
    const handleMouseMove = (e: MouseEvent) => {
      const now = Date.now();
      const dt = now - lastTime;
      const dy = e.clientY - lastY;
      // Mouse near top of viewport
      if (e.clientY < 20) {
        triggerExitIntent();
      }
      // Fast upward movement
      if (dt > 0 && dt < 200 && dy < -80) {
        triggerExitIntent();
      }
      lastY = e.clientY;
      lastTime = now;
    };
    const handleVisibilityChange = () => {
      if (document.hidden) {
        triggerExitIntent();
      }
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dismissed, triggerExitIntent]);
  // Exit intent: Mobile — rapid scroll up + inactivity
  useEffect(() => {
    if (dismissed) return;
    let lastScrollY = window.scrollY;
    let lastScrollTime = Date.now();
    const handleScroll = () => {
      const now = Date.now();
      const dy = window.scrollY - lastScrollY;
      const dt = now - lastScrollTime;
      // Rapid upward scroll
      if (dt > 0 && dt < 300 && dy < -200) {
        triggerExitIntent();
      }
      lastScrollY = window.scrollY;
      lastScrollTime = now;
      // Reset inactivity timer on scroll
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = setTimeout(() => {
        triggerExitIntent();
      }, 20000);
    };
    const handleTouch = () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = setTimeout(() => {
        triggerExitIntent();
      }, 20000);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    window.addEventListener('touchstart', handleTouch, {
      passive: true
    });
    // Start initial inactivity timer
    inactivityTimerRef.current = setTimeout(() => {
      triggerExitIntent();
    }, 20000);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('touchstart', handleTouch);
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    };
  }, [dismissed, triggerExitIntent]);
  if (dismissed) return null;
  const toastContent = <AnimatePresence data-id="element-628">
      {isVisible && <motion.div initial={{
      opacity: 0,
      x: -20
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: -20
    }} transition={{
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }} style={{
      position: 'fixed',
      zIndex: 999999,
      bottom: isMobile ? 16 : 20,
      left: isMobile ? 16 : 20,
      right: isMobile ? 16 : 'auto',
      width: isMobile ? 'calc(100% - 32px)' : 'auto',
      maxWidth: isMobile ? 'none' : 380,
      minWidth: isMobile ? 'auto' : 280,
      boxSizing: 'border-box' as const,
      overflow: 'visible' as const
    }} data-id="element-629">
          <div style={{
        boxSizing: 'border-box',
        overflow: 'visible',
        wordWrap: 'break-word',
        whiteSpace: 'normal'
      }} className="relative bg-neutral-900/95 backdrop-blur-md border border-neutral-800/60 rounded-xl shadow-2xl shadow-black/50" data-id="element-630">
            {/* Subtle top glow */}
            <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-teal-500/40 to-transparent" data-id="element-631" />

            <div className="flex items-start gap-3 p-3.5 pr-9" data-id="element-632">
              {/* Thumbnail */}
              {thumbnailSrc && <div className="flex-shrink-0 w-11 h-11 rounded-lg overflow-hidden bg-neutral-800 border border-neutral-700/50" data-id="element-633">
                  <img src={thumbnailSrc} alt="" className="w-full h-full object-cover" onError={e => {
              ;
              (e.target as HTMLImageElement).style.display = 'none';
            }} data-id="element-634" />
                </div>}

              {/* Text block */}
              <div className="flex-1 min-w-0" style={{
            lineHeight: 1.25
          }} data-id="element-635">
                {/* Line 1: ● Recent Purchase */}
                <div className="flex items-center gap-1.5 mb-1" data-id="element-636">
                  <span className="relative flex h-1.5 w-1.5" data-id="element-637">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" data-id="element-638" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-teal-400" data-id="element-639" />
                  </span>
                  <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-teal-400" data-id="element-640">
                    Recent Purchase
                  </span>
                </div>

                {/* Line 2: Name from City, ST */}
                <p className="text-white text-[13px] font-medium truncate" style={{
              lineHeight: 1.3
            }} data-id="element-641">
                  {buyer.name}{' '}
                  <span className="text-neutral-500 font-normal" data-id="element-642">from</span>{' '}
                  {buyer.city}, {buyer.state}
                </p>

                {/* Line 3: Edition · $Price */}
                <p className="text-neutral-400 text-[11px] mt-0.5 truncate" style={{
              lineHeight: 1.3
            }} data-id="element-643">
                  {detectedEdition} <span className="text-neutral-600" data-id="element-644">·</span>{' '}
                  <span className="text-teal-400 font-semibold" data-id="element-645">
                    {detectedPrice}
                  </span>
                </p>
              </div>
            </div>

            {/* Close button */}
            <button onClick={handleClose} aria-label="Dismiss notification" className="absolute top-2.5 right-2.5 w-5 h-5 flex items-center justify-center rounded text-neutral-600 hover:text-white transition-colors" data-id="element-646">
              <XIcon className="w-3.5 h-3.5" data-id="element-647" />
            </button>
          </div>
        </motion.div>}
    </AnimatePresence>;
  return createPortal(toastContent, document.body);
}